import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Panel } from "@/components/ui-bits";

export default function LocationsPanel() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", notes: "" });
  const [editId, setEditId] = useState(null);

  const load = async () => setRows((await api.get("/locations")).data);
  useEffect(() => {
    load();
  }, []);

  const startNew = () => { setEditId(null); setForm({ name: "", notes: "" }); setOpen(true); };
  const startEdit = (l) => { setEditId(l.id); setForm({ name: l.name, notes: l.notes || "" }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editId) await api.patch(`/locations/${editId}`, form);
      else await api.post("/locations", form);
      toast.success("Saved"); setOpen(false); load();
    } catch { toast.error("Failed"); }
  };
  const remove = async (id) => { if (confirm("Delete?")) { await api.delete(`/locations/${id}`); load(); } };

  return (
    <Panel title={`Locations (${rows.length})`} action={<Button className="h-11 gap-2" onClick={startNew} data-testid="new-location-button"><Plus className="h-4 w-4" /> New location</Button>}>
      <table className="w-full" data-testid="locations-table">
        <thead className="text-left text-xs uppercase tracking-wider text-stone-500"><tr><th className="py-3">Name</th><th>Notes</th><th /></tr></thead>
        <tbody>
          {rows.map((l) => (
            <tr key={l.id} className="border-t border-border h-16">
              <td className="font-medium">{l.name}</td>
              <td className="text-stone-600 text-sm">{l.notes}</td>
              <td className="flex justify-end gap-1">
                <Button size="icon" variant="ghost" onClick={()=>startEdit(l)}><Pencil className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" onClick={()=>remove(l.id)}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="location-dialog">
          <DialogHeader><DialogTitle className="font-display">{editId ? "Edit" : "New"} location</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div><Label>Name</Label><Input className="h-12 mt-1" required value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} data-testid="location-name" /></div>
            <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e)=>setForm({...form, notes: e.target.value})} /></div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={()=>setOpen(false)}>Cancel</Button>
              <Button type="submit" className="h-12 px-6" data-testid="location-submit">Save</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Panel>
  );
}
